import React, { useEffect, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup/dist/yup"
import { Box, Button, Grid, Stack, Typography } from "@mui/material"
import { useMutationStudentFinalize } from "api/reactQuery/mutations/students"
import { useQueryGetStudentFinalize } from "api/reactQuery/queries/students"
import ClassCode from "components/common/classCode"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import Cookies from "js-cookie"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { RoutePath } from "routes/Route.types"
import { selectDataAuth } from "store/auth/auth.selectors"
import { setActivePageSignUp, setAuthData } from "store/auth/auth.slice"
import { InviteCodeState } from "ts/types/InviteCode"
import { getErrorMessageFromTab } from "utils/api"
import { clearAuthDataLocalStorage } from "utils/authData"
import { getAuthCookieDomain } from "utils/domain"

import { validateSchema, defaultValues } from "./Finalization.config"

interface IFormData {
  first_name: string
  last_name: string
  invitation_code: string
}

const Finalization = () => {
  const dispatch = useAppDispatch()
  const authData = useAppSelector(selectDataAuth)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSnackbar } = useCustomSnackbar()

  const [inviteCode, setInviteCode] = useState<InviteCodeState>({
    no1: "",
    no2: "",
    no3: "",
    no4: "",
    no5: "",
    no6: "",
  })

  const methods = useForm({
    resolver: yupResolver(validateSchema),
    defaultValues: defaultValues,
  })

  const { data } = useQueryGetStudentFinalize()

  useEffect(() => {
    if (data) {
      methods.setValue("first_name", data?.data.first_name)
      methods.setValue("last_name", data?.data.last_name)
    }
  }, [data])

  const { mutate: finalize } = useMutationStudentFinalize({
    options: {
      onSuccess: () => {
        dispatch(
          setAuthData({
            ...authData,
            finalized: true,
          })
        )
        showSnackbar({
          title: t("noAuthFlow.loginSuccessful"),
        })
        navigate(RoutePath.HOMEPAGE)
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const onSubmit = (value: IFormData) => {
    finalize({
      first_name: value.first_name,
      last_name: value.last_name,
      invitation_code: inviteCode.no1.concat(
        inviteCode.no2,
        inviteCode.no3,
        inviteCode.no4,
        inviteCode.no5,
        inviteCode.no6
      ),
    })
  }

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <FormProvider {...methods}>
        <Stack textAlign="left">
          <Typography variant="body1" color="primary.main" fontWeight="600">
            {t("noAuthFlow.student")}
          </Typography>
          <Typography
            component="h1"
            tabIndex={-1}
            variant="h2"
            color="mockup.neutral50"
            sx={{ pt: 1, pb: 4 }}
          >
            {t("noAuthFlow.fillUpDetails")}
          </Typography>
          <Stack sx={{ mb: 3 }}>
            <ClassCode
              label={t("noAuthFlow.enterYourClassCode")}
              setInviteCode={setInviteCode}
            />
          </Stack>
          <Grid container spacing={3} mb={3}>
            <Grid size={{ tablet: 6, mobile: 12 }}>
              <TextField name="first_name" label={t("noAuthFlow.firstName")} />
            </Grid>
            <Grid size={{ tablet: 6, mobile: 12 }}>
              <TextField name="last_name" label={t("noAuthFlow.lastName")} />
            </Grid>
          </Grid>
        </Stack>
        <Box display="flex" columnGap="10px" mt="10px">
          <Button
            onClick={() => {
              clearAuthDataLocalStorage()
              Cookies.remove("auth_data", { domain: getAuthCookieDomain() })
              dispatch(setAuthData(null))
              dispatch(setActivePageSignUp(0))
              navigate(RoutePath.LOGIN)
            }}
            sx={{
              width: "max-content",
            }}
            variant="outlined"
          >
            {t("noAuthFlow.back")}
          </Button>
          <Button type="submit" fullWidth>
            {t("noAuthFlow.continue")}
          </Button>
        </Box>
      </FormProvider>
    </form>
  )
}

export default Finalization
